import { IntlService } from '@progress/kendo-angular-intl';
import { ColumnSettings } from '../model/column-settings';
import { KendoMessageService } from '../service/kendo-message.service';
import { cloneDeep } from 'lodash';
import { TranslateService } from '@ngx-translate/core';

export class GridQueryUtils
{
    public static generateQueryString(state: any, root: string): string
    {
        let url = '';
        let sortParam = '';

        // Pagination
        const page = state.skip / state.take;
        url = root + '?page=' + page + '&size=' + state.take;

        // Sort Information
        if (state.sort && state.sort.length > 0)
        {
            if (state.sort[0].dir !== undefined)
            {
                sortParam = state.sort[0].field + ',' + state.sort[0].dir;
                url = url + '&sort=' + sortParam;
            }
        }
        // Filter Information
        // Strings: contains,doesnotcontain,eq,neq,endswith,startswith,isnull,isnotnull,isempty,isnotempty
        // Dates: eq, neq, gte, gt, lte, lt, isnull
        if (state.filter && state.filter.filters.length > 0)
        {
            let filterParam = '';
            let filterLogic = state.filter.logic;

            state.filter.filters.forEach(filterItem =>
            {
                if (filterItem.field !== undefined)
                {
                    if (filterItem.logic !== undefined)
                    {
                        filterLogic = filterItem.logic;
                    }
                    filterParam += ((filterParam === '') ? '' : ',') + filterItem.field + ':' + filterItem.operator + ':"'
                        + this.getFormattedValueString(filterItem) + '":' + filterLogic;
                }

                if (filterItem.filters)
                {
                    filterItem.filters.forEach(subFilterItem =>
                    {
                        filterParam += ((filterParam === '') ? '' : ',') + subFilterItem.field + ':' + subFilterItem.operator + ':' +
                            '"' + this.getFormattedValueString(subFilterItem) + '":' + filterItem.logic;
                    });
                }
            });
            url = url + '&search=' + filterParam;
        }
        return url;
    }

    /**
     * This method is to set stored filters in string format and convert back to JS date.
     *
     * @param filters Filters array.
     */
    public static convertFiltersToJSDates(filters: any, columnsConfig: ColumnSettings[]): void
    {
        filters.forEach((data: any) =>
        {
            if (data.filters !== undefined)
            {
                this.convertFiltersToJSDates(data.filters, columnsConfig);    // Recursive call
            }
            else
            {
                const columnDef = columnsConfig.find((col) => col.field === data.field);
                if (columnDef !== undefined && columnDef.filter === 'date' && data.value !== null)
                {
                    data.value = new Date(data.value);
                }
            }
        });
    }


    /**
     * This is base method for applying grid settings configuration to loaded filter columns.
     *
     * @param filterColumns Loaded columns filter property
     * @param columnsConfig  Static Columns configuration
     * @param patientLabel Patient label text
     * @returns Final ColumnSettings for the grid
     */
    public static setBaseColumnsSetting(translateService: TranslateService,
        filterColumns: string, columnsConfig: ColumnSettings[], patientLabel: string = null): ColumnSettings[]
    {
        // Add any new columns to saved filter columns
        const loadedColumns = filterColumns ? JSON.parse(filterColumns) : columnsConfig;
        for (let i = 0; i < columnsConfig.length; i++)
        {
            if (loadedColumns.findIndex((x: ColumnSettings) => x.field === columnsConfig[i].field) === -1)
            {
                loadedColumns.push(columnsConfig[i]);
            }
        }
        // Apply any updated setting and translations
        for (let i = 0; i < loadedColumns.length; i++)
        {
            const foundColumn = columnsConfig.find(x => x.field === loadedColumns[i].field);
            if (foundColumn)
            {
                loadedColumns[i].title = foundColumn.title;
                loadedColumns[i].titleKeys = foundColumn.titleKeys;
                loadedColumns[i].sortable = foundColumn.sortable;
                loadedColumns[i].filterable = foundColumn.filterable;
                loadedColumns[i].columnMenu = foundColumn.columnMenu;
                loadedColumns[i].filter = foundColumn.filter;
                loadedColumns[i].format = foundColumn.format;
                loadedColumns[i].decimals = foundColumn.decimals;
                loadedColumns[i].class = foundColumn.class;
            }
            if (loadedColumns[i].titleKeys)
            {
                const title = translateService.instant(loadedColumns[i].titleKeys[0]);
                loadedColumns[i].title = patientLabel ? title.replace('{{patientLabel}}', patientLabel) : title;
                // Account for Patient Import items columns that replace PCP or Contact for many fields
                if (loadedColumns[i].titleKeys.length === 2 && loadedColumns[i].title.indexOf('{type}') > -1)
                {
                    loadedColumns[i].title = loadedColumns[i].title.replace('{type}',
                        translateService.instant(loadedColumns[i].titleKeys[1]));
                }
            }
        }
        return loadedColumns;
    }

    /**
     * Translate recursively the filter state to translated conditions.
     *
     * @param filters Inbound filter object.
     * @param columnsConfig Column configuration to identify the type of columns for date conversions.
     * @param logic Filter logic.
     * @param text Accumulated conditions text.
     * @param intl Kendo intl service for date formatting.
     * @param kendoMessageService Kendo message service to translate conditions.
     * @returns Translated filter conditions to display text.
     */
    public static filtersToText(filters: any, columnsConfig: ColumnSettings[], logic: string, text: string,
        intl: IntlService, kendoMessageService: KendoMessageService): string
    {
        let colCount = 0;
        let endOfFilters = false;
        filters.forEach((data: any) =>
        {
            if (data.filters !== undefined)
            {
                text = this.filtersToText(data.filters, columnsConfig, data.logic, text, intl, kendoMessageService);    // Recursive call
            }
            else
            {
                const columnDef = columnsConfig.find((col) => col.field === data.field);
                if (columnDef !== undefined && columnDef.filter === 'date' && data.value !== null)
                {
                    data.value = intl.formatDate(new Date(data.value), columnDef.dataType === 'date' ? 'd' : 'g');
                }

                const formattedLogic = this.getConditionText(logic, kendoMessageService);
                text += text !== '' ? ` ${formattedLogic} ` : '';

                // Opening condition
                text += (colCount === 0) ? '(' : '';

                // Set field and condition
                text += `${data.field} ${this.getConditionText(data.operator, kendoMessageService)}`;

                // Set value
                text += (data.value !== null) ? ` '${data.value}'` : '';

                // Closing condition
                text += (filters.length > 0 && colCount === filters.length - 1) ? ')' : '';

                colCount++;
                endOfFilters = true;
            }
        });
        // Adding last closing if missing
        if (endOfFilters && text.length > 0 && text[text.length - 1] !== ')')
        {
            text += ')';
        }
        return text;
    }

    /**
     * This function is to normalize the filter object and moved into a filters object to make sure the back-end would deseralized properly.
     *
     * @param state Input state filter object.
     * @returns Converted state.filter object.
     */
    public static normalizeFilters(state: any): any
    {
        // Rename filters to filtersRoot
        const data = cloneDeep(state);
        data.filter.filtersRoot = data.filter.filters;
        delete data.filter.filters;

        // Convert single filter object to filters array
        const updatedFilters = cloneDeep(data);
        delete updatedFilters.filter;

        updatedFilters.filter = {};
        updatedFilters.filter.filtersRoot = [];

        if (data.filter && data.filter.filtersRoot.length > 0)
        {
            data.filter.filtersRoot.forEach(filterItem =>
            {
                if (filterItem.field !== undefined)
                {
                    const filtersData: any = {};
                    filtersData.filters = [];
                    filtersData.filters.push(filterItem);
                    filtersData.logic = 'and';
                    updatedFilters.filter.filtersRoot.push(filtersData);
                }
                if (filterItem.filters)
                {
                    updatedFilters.filter.filtersRoot.push(filterItem);
                }
            });
        }
        return updatedFilters;
    }

    protected static getFormattedValueString(filterItem): any
    {
        if (filterItem === '')
        {
            return filterItem;
        }
        if (filterItem.value instanceof Date)
        {
            if (filterItem.field === 'dateOfBirth' ||
                filterItem.field === 'startDate' || filterItem.field === 'endDate' || filterItem.field === 'billableDate')
            {
                const dob = new Date(filterItem.value.getFullYear(),
                    filterItem.value.getMonth(),
                    filterItem.value.getDate(), 12, 0, 0, 0);
                return `${dob.toISOString().substring(0, 10)}T12:00:00`;
            }
            else
            {
                // Grid filter is using date picker only and therefore we must include the full day of the selected date.
                if (filterItem.value.getHours() === 0 &&
                    filterItem.value.getMinutes() === 0 &&
                    filterItem.value.getSeconds() === 0)
                {
                    let hour = 0; let min = 0; let sec = 0;
                    if (filterItem.operator === 'lte' || filterItem.operator === 'gt')
                    {
                        hour = 23;
                        min = 59;
                        sec = 59;
                    }
                    const dateTimeFilter = new Date(filterItem.value.getFullYear(),
                        filterItem.value.getMonth(),
                        filterItem.value.getDate(), hour, min, sec);

                    return dateTimeFilter.toISOString();
                }
                else
                {
                    return filterItem.value.toISOString();
                }
            }
        }
        if (typeof filterItem.value === 'number')
        {
            return filterItem.value;
        }
        if (typeof filterItem.value === 'boolean')
        {
            return filterItem.value;
        }
        let str = filterItem.value;
        // escaping double quotes as they're meaningful to the backend parsing.
        if (str !== null)
        {
            str = str.replace(/"/g, '\\"');
            return encodeURI(str);
        }
        else
        {
            return '';
        }
    }

    /**
     * Provides the translation of query string condition and returned the translated text.
     *
     * @param condition Query condition
     * @param kendoMessageService Kendo message service that provide the translated condition.
     * @returns string Translated condition text.
     */
    private static getConditionText(condition: string, kendoMessageService: KendoMessageService): string
    {
        switch (condition)
        {
            case 'and':
                return kendoMessageService.get('kendo.grid.filterAndLogic');
            case 'or':
                return kendoMessageService.get('kendo.grid.filterOrLogic');
            case 'eq':
                return kendoMessageService.get('kendo.grid.filterEqOperator');
            case 'gt':
                return kendoMessageService.get('kendo.grid.filterGtOperator');
            case 'lt':
                return kendoMessageService.get('kendo.grid.filterLtOperator');
            case 'neq':
                return kendoMessageService.get('kendo.grid.filterNotEqOperator');
            case 'gte':
                return kendoMessageService.get('kendo.grid.filterGteOperator');
            case 'lte':
                return kendoMessageService.get('kendo.grid.filterLteOperator');
            case 'contains':
                return kendoMessageService.get('kendo.grid.filterContainsOperator');
            case 'doesnotcontain':
                return kendoMessageService.get('kendo.grid.filterNotContainsOperator');
            case 'startswith':
                return kendoMessageService.get('kendo.grid.filterStartsWithOperator');
            case 'endswith':
                return kendoMessageService.get('kendo.grid.filterEndsWithOperator');
            case 'isnull':
                return kendoMessageService.get('kendo.grid.filterIsNullOperator');
            case 'isnotnull':
                return kendoMessageService.get('kendo.grid.filterIsNotNullOperator');
            case 'isempty':
                return kendoMessageService.get('kendo.grid.filterIsEmptyOperator');
            case 'isnotempty':
                return kendoMessageService.get('kendo.grid.filterIsNotEmptyOperator');
            default:
                return condition;
        }
    }
}
